import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ViewChild, AfterViewInit, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDeleteDialogComponent } from 'src/app/shared/confirm-delete-dialog/confirm-delete-dialog.component';
import {
  SurveyApprovalAction,
  SurveyApprovalDialogComponent,
  SurveyApprovalDialogData,
} from 'src/app/shared/survey-approval-dialog/survey-approval-dialog.component';

@Component({
  selector: 'app-survey-list',
  templateUrl: './survey-list.component.html',
  styleUrl: './survey-list.component.css',
})
export class SurveyListComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [
    'srNo',
    'projectName',
    'surveyLead',
    'lrsCorridor',
    'documents',
    'startDate',
    'endDate',
    'status',
    'action',
  ];

  dataSource = new MatTableDataSource<Record<string, unknown>>([]);
  searchKeyword = '';
  statusFilter = '';
  processingId: number | null = null;
  projectId: string | null = null;
  projectName = '';
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private apiService: ApiService,
    private toastrMsgService: ToastrMsgService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  get pageTitle(): string {
    return this.projectName
      ? `${this.projectName} — Survey Details`
      : 'Survey Management';
  }

  get addSurveyQueryParams(): Record<string, string> {
    if (!this.projectId) return {};
    const params: Record<string, string> = { projectId: this.projectId };
    if (this.projectName) params['projectName'] = this.projectName;
    return params;
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((qp) => {
      this.projectId = qp.get('projectId') || qp.get('project_id');
      this.projectName = (qp.get('projectName') || '').trim();
      this.fetchData();
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  applyFilter(event: Event): void {
    this.searchKeyword = (event.target as HTMLInputElement).value.trim();
    this.fetchData();
  }

  filterByStatus(event: Event): void {
    this.statusFilter = (event.target as HTMLSelectElement).value;
    this.fetchData();
  }

  fetchData(): void {
    this.apiService
      .listProjectSurveyStages({
        search: this.searchKeyword || undefined,
        status: this.statusFilter || undefined,
        project_id: this.projectId || undefined,
        limit: 500,
      })
      .subscribe({
        next: (res: { data?: Record<string, unknown>[] }) => {
          this.dataSource.data = res.data || [];
        },
        error: (err) => {
          this.toastrMsgService.error(err?.error?.message || err?.error?.errorMessage || 'Load failed');
        },
      });
  }

  deleteRecord(item: { id: number; project_name?: string }): void {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      data: { itemName: item.project_name || 'survey record' },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.apiService.deleteProjectSurveyStage(item.id).subscribe({
          next: () => {
            this.toastrMsgService.success('Survey deleted');
            this.fetchData();
          },
          error: (err) =>
            this.toastrMsgService.error(err?.error?.message || err?.error?.errorMessage || 'Delete failed'),
        });
      }
    });
  }

  formatLrsCorridor(row: Record<string, unknown>): string {
    const lrs = this.surveyLrs(row);
    if (!lrs['route_id']) return '—';
    const parts: string[] = [`Route #${lrs['route_id']}`];
    if (lrs['chainage']) parts.push(String(lrs['chainage']));
    else if (lrs['from_km'] != null && lrs['to_km'] != null) {
      parts.push(`${lrs['from_km']}–${lrs['to_km']} km`);
    }
    return parts.join(' · ');
  }

  openSurveyOnMap(row: Record<string, unknown>): void {
    const lrs = this.surveyLrs(row);
    const queryParams: Record<string, string | number> = { tab: 'lrs' };
    if (lrs['route_id']) queryParams['route_id'] = Number(lrs['route_id']);
    if (lrs['from_km'] != null) queryParams['from_km'] = Number(lrs['from_km']);
    if (lrs['to_km'] != null) queryParams['to_km'] = Number(lrs['to_km']);
    if (row['project_id']) queryParams['project_id'] = Number(row['project_id']);
    void this.router.navigate(['/home/geospatial'], { queryParams });
  }

  surveyDocuments(row: Record<string, unknown>): { name: string; url: string }[] {
    const raw = row['documents'];
    const parsed = Array.isArray(raw) ? raw : this.parseJsonArray(raw);
    return parsed
      .map((doc) => {
        if (!doc || typeof doc !== 'object') return null;
        const item = doc as Record<string, unknown>;
        const url = String(item['url'] || item['file_url'] || '');
        if (!url) return null;
        return { name: String(item['name'] || item['file_name'] || 'Report'), url };
      })
      .filter((doc): doc is { name: string; url: string } => !!doc);
  }

  getSurveyStatus(row: Record<string, unknown>): string {
    return String(row['status'] || 'pending').toLowerCase();
  }

  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      pending: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      draft: 'bg-gray-100 text-gray-800',
      submitted_for_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      revision_required: 'bg-orange-100 text-orange-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  }

  canSubmit(row: Record<string, unknown>): boolean {
    return ['pending', 'in_progress', 'revision_required', 'draft'].includes(this.getSurveyStatus(row));
  }

  canReview(row: Record<string, unknown>): boolean {
    if (this.getSurveyStatus(row) !== 'submitted_for_approval') return false;
    const user = this.currentUser();
    if (user['type'] === 1) return true;
    if (user['type'] !== 2) return false;
    return Number(row['org_id']) === Number(this.callerOrgId);
  }

  openApprovalDialog(row: Record<string, unknown>, action: SurveyApprovalAction): void {
    const dialogRef = this.dialog.open(SurveyApprovalDialogComponent, {
      width: '480px',
      data: {
        projectName: String(row['project_name'] || 'Survey'),
        action,
        currentStatus: this.getSurveyStatus(row),
      } as SurveyApprovalDialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      const id = Number(row['id']);
      this.processingId = id;
      this.apiService.processSurveyApproval(id, { action, notes: result.notes }).subscribe({
        next: (res: { message?: string; boq?: { created?: boolean; boq?: { boq_code?: string } | null } }) => {
          let msg = `Survey ${action.replace('_', ' ')} processed`;
          if (action === 'approve') {
            if (res?.boq?.created && res.boq.boq?.boq_code) {
              msg = `Survey approved · BOQ ${res.boq.boq.boq_code}`;
            } else if (res?.boq && !res.boq.boq) {
              msg = 'Survey approved · No BOQ to version';
            }
          }
          this.toastrMsgService.success(msg);
          this.processingId = null;
          this.fetchData();
        },
        error: (err) => {
          this.processingId = null;
          this.toastrMsgService.error(err?.error?.message || 'Approval action failed');
        },
      });
    });
  }

  isProcessing(row: Record<string, unknown>): boolean {
    return this.processingId === Number(row['id']);
  }

  private parseJsonField(raw: unknown): Record<string, unknown> {
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
    try {
      const parsed = JSON.parse(String(raw));
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  private parseJsonArray(raw: unknown): unknown[] {
    if (Array.isArray(raw)) return raw;
    if (!raw) return [];
    try {
      const parsed = JSON.parse(String(raw));
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private surveyLrs(row: Record<string, unknown>): Record<string, unknown> {
    const remarks = this.parseJsonField(row['remarks']);
    const lrs = (remarks['lrs'] as Record<string, unknown>) || {};
    const deviation = (remarks['lrs_deviation'] as Record<string, unknown>) || {};
    if (lrs['route_id']) return lrs;
    if (deviation['route_id']) {
      return {
        route_id: deviation['route_id'],
        from_km: deviation['from_km'],
        to_km: deviation['to_km'],
        chainage: deviation['chainage'],
      };
    }
    return {};
  }

  private currentUser(): Record<string, unknown> {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}') as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  private get callerOrgId(): number | null {
    const user = this.currentUser();
    if (user['type'] === 2) return Number(user['id']);
    const orgId = user['org_id'];
    return orgId != null ? Number(orgId) : null;
  }
}
